// What an Undo writes back for a deleted list item (UI audit a2-14).
//
// Dependency-free so the root suite can test it; the write itself is
// `restoreItem` in `db.ts`.
//
// Each list kind had its own `restore` that copied the columns its author
// thought of — and every one left out `id` and `created_at`, so an undone
// delete came back as a new row: a new id, a new timestamp, and on a list
// ordered by `position` (where an aisle's items routinely share one) a new
// place. "Body wash, Toothpaste" came back "Toothpaste, Body wash". The row
// is now returned whole, under its own id, so it lands where it was and
// anything that pointed at it (a subtask's `parent_id`) still does.

import type { ListItem } from './types'

/** A list item as `select('*')` returns it: the declared columns plus the two
 *  the client never reads but the table has. */
export type StoredListItem = ListItem & { created_at?: string; user_id?: string }

/**
 * Every column the row had, except `user_id`, which the table fills from the
 * session (and RLS checks) — a client should not be the one asserting it.
 * Spread rather than listed, so a column added to the table later comes back
 * on Undo without anyone remembering to add it here, which is how six
 * hand-written lists each came to miss a different one.
 */
export function restoreRow(i: StoredListItem): Omit<StoredListItem, 'user_id'> {
  const { user_id: _owner, ...row } = i
  return row
}
